import { useMemo, useState } from "react";
import { observer } from "mobx-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Panel } from "@/components/ui/panel";
import { validatePassword } from "@/helpers/password.helper";
import { getPasswordStrength } from "@/helpers/password-strength.helper";
import { cn } from "@/lib/utils";
import { useStore } from "@/stores/store-context";
import { requestErrorSummary } from "@/lib/errors/request-error-summary";
import { useReportUnsavedEdits } from "@/hooks/use-report-unsaved-edits";

const STRENGTH_BAR_CLASSES = [
  "bg-destructive",
  "bg-destructive/80",
  "bg-warning",
  "bg-success/80",
  "bg-success",
];

const MIN_STRENGTH = 3;

export const SecurityForm = observer(function SecurityForm({
  onDirtyChange,
}: {
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { t } = useTranslation();
  const { user } = useStore();
  const isPasswordAutoset = user.currentUser?.is_password_autoset ?? false;

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Any typed password is unsaved until "Update password" succeeds, which clears all
  // three fields; a failed change keeps them, so the window still asks (NG-2924).
  useReportUnsavedEdits(
    currentPassword !== "" || newPassword !== "" || confirmPassword !== "",
    onDirtyChange,
  );

  const inputType = showPassword ? "text" : "password";
  const toggleLabel = showPassword ? t("auth.password_hide") : t("auth.password_show");

  const strength = getPasswordStrength(newPassword);

  const disabledReason = useMemo<string | null>(() => {
    if (!isPasswordAutoset && currentPassword.length === 0) {
      return "profile_settings.security.current_required";
    }
    const validation = validatePassword(newPassword, confirmPassword);
    if (validation) return validation;
    if (strength < MIN_STRENGTH) return "profile_settings.security.weak_password";
    return null;
  }, [isPasswordAutoset, currentPassword, newPassword, confirmPassword, strength]);

  const handleSubmit = async () => {
    if (disabledReason) return;
    setSaving(true);
    try {
      await user.changePassword(isPasswordAutoset ? undefined : currentPassword, newPassword);
      toast.success(t("profile_settings.security.save_success"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("[profile] password change failed", requestErrorSummary(error));
      toast.error(t("profile_settings.security.save_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel className="space-y-6">
      {!isPasswordAutoset ? (
        <div className="space-y-2">
          <Label htmlFor="security-current-password">
            {t("profile_settings.security.current_password_label")}
          </Label>
          <div className="relative">
            <Input
              id="security-current-password"
              type={inputType}
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              disabled={saving}
              className="pr-16"
            />
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute top-1/2 right-2 h-auto -translate-y-1/2 px-2 py-1 text-xs text-muted-foreground"
            >
              {toggleLabel}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          {t("profile_settings.security.autoset_hint")}
        </p>
      )}

      <div className="space-y-2">
        <Label htmlFor="security-new-password">
          {t("profile_settings.security.new_password_label")}
        </Label>
        <div className="relative">
          <Input
            id="security-new-password"
            type={inputType}
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            disabled={saving}
            className="pr-16"
          />
          <Button
            type="button"
            variant="ghost"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute top-1/2 right-2 h-auto -translate-y-1/2 px-2 py-1 text-xs text-muted-foreground"
          >
            {toggleLabel}
          </Button>
        </div>
        <div
          className="flex h-1 w-full gap-1 rounded-full bg-muted"
          aria-label={t("profile_settings.security.strength_label")}
          data-testid="password-strength"
          data-strength={strength}
        >
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={cn(
                "h-1 flex-1 rounded-full",
                i < strength ? STRENGTH_BAR_CLASSES[strength] : "bg-transparent",
              )}
            />
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {t(`profile_settings.security.strength_${strength}`)}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="security-confirm-password">
          {t("profile_settings.security.confirm_password_label")}
        </Label>
        <div className="relative">
          <Input
            id="security-confirm-password"
            type={inputType}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            disabled={saving}
            className="pr-16"
          />
          <Button
            type="button"
            variant="ghost"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute top-1/2 right-2 h-auto -translate-y-1/2 px-2 py-1 text-xs text-muted-foreground"
          >
            {toggleLabel}
          </Button>
        </div>
        {disabledReason ? (
          <p className="text-xs text-muted-foreground">{t(disabledReason)}</p>
        ) : null}
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={saving || disabledReason !== null}
        >
          {saving ? t("profile_settings.security.saving") : t("profile_settings.security.save")}
        </Button>
      </div>
    </Panel>
  );
});
